import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Package,
  Building2,
  Receipt,
  Gift,
  Star,
  User,
  Package as PackageIcon,
  AlertTriangle,
  Trophy,
} from 'lucide-react';
import { useAuth } from '../../auth/context/AuthContext';
import { useSocket } from '../../realtime/context/SocketContext';
import ThemeToggle from '../../../app/components/ThemeToggle';
import ActivePromotionsBanner from '../../shared/ActivePromotionsBanner';

const STATUS_LABEL = {
  at_warehouse: { label: 'At Warehouse', cls: 'badge-at_warehouse' },
  in_transit: { label: 'In Transit', cls: 'badge-in_transit' },
  arrived_ghana: { label: 'In Ghana', cls: 'badge-arrived_ghana' },
  ready_for_pickup: { label: 'Ready for Pickup', cls: 'badge-ready_for_pickup' },
  out_for_delivery: { label: 'Out for Delivery', cls: 'badge-out_for_delivery' },
  delivered: { label: 'Delivered', cls: 'badge-delivered' },
};

const TIER_COLOR = { gold: '#b45309', silver: '#475569', bronze: '#9a5b28' };

const QUICK_CARDS = [
  { icon: Package, label: 'My Items', sub: 'View all shipments', to: '/items' },
  { icon: Building2, label: 'My Addresses', sub: 'CN & TR warehouse', to: '/addresses' },
  { icon: Receipt, label: 'Receipts', sub: 'Download receipts', to: '/receipts' },
  { icon: Gift, label: 'Refer & Earn', sub: 'Earn referral points', to: '/refer' },
  { icon: Star, label: 'Rewards', sub: 'Loyalty and tiers', to: '/loyalty' },
  { icon: User, label: 'Profile', sub: 'Account settings', to: '/profile' },
];

export default function CustomerHome() {
  const { user } = useAuth();
  const socket = useSocket();
  const [summary, setSummary] = useState(null);
  const [promos, setPromos] = useState([]);

  const loadDashboard = () =>
    axios
      .get('/api/customer/dashboard')
      .then((response) => setSummary(response.data))
      .catch(() => {});

  const loadPromotions = () =>
    axios
      .get('/api/promotions')
      .then((response) => setPromos(response.data))
      .catch(() => {});

  useEffect(() => {
    loadDashboard();
    loadPromotions();
  }, []);

  useEffect(() => {
    if (!socket) return undefined;

    const onItemReceived = () => {
      toast.success('New items arrived at the warehouse.');
      loadDashboard();
    };
    const onPaymentVerified = (payload) => {
      toast.success(`Payment of GHS ${payload.amount} verified.`);
      loadDashboard();
    };
    const onPaymentRejected = () => {
      toast.error('Payment rejected. Please re-submit.');
      loadDashboard();
    };
    const onStatusUpdated = () => {
      loadDashboard();
    };
    const onPromoNew = (payload) => {
      toast.success(`New promotion: ${payload.title} - ${payload.discount_percent}% off.`);
      loadPromotions();
    };

    socket.on('item:received', onItemReceived);
    socket.on('payment:verified', onPaymentVerified);
    socket.on('payment:rejected', onPaymentRejected);
    socket.on('status:updated', onStatusUpdated);
    socket.on('promo:new', onPromoNew);

    return () => {
      socket.off('item:received', onItemReceived);
      socket.off('payment:verified', onPaymentVerified);
      socket.off('payment:rejected', onPaymentRejected);
      socket.off('status:updated', onStatusUpdated);
      socket.off('promo:new', onPromoNew);
    };
  }, [socket]);

  const unpaid = parseFloat(summary?.unpaidFees || 0);
  const totalPaid = parseFloat(summary?.totalPaid || 0);
  const tier = user?.loyalty_tier || 'bronze';
  const recent = summary?.recentItems || [];

  return (
    <div className="ch-page">
      <div className="ch-header">
        <div className="ch-header-row">
          <div>
            <p className="ch-greeting-sub">Welcome back</p>
            <p className="ch-greeting-name">{user?.full_name}</p>
          </div>
          <div className="ch-header-controls">
            <ThemeToggle />
            <div className="ch-mobile-controls mobile-only-flex">
              <Link to="/profile" className="ch-avatar" aria-label="Open profile">
                <User size={18} strokeWidth={1.8} />
              </Link>
            </div>
          </div>
        </div>

        <div className="ch-header-meta">
          <div className="ch-tier-badge" style={{ '--tier-color': TIER_COLOR[tier] }}>
            <Trophy size={14} strokeWidth={2} />
            <span className="ch-tier-name">{tier} Member</span>
            <span className="ch-tier-pts">- {user?.loyalty_points || 0} pts</span>
          </div>
          <div className="ch-mark-chip">{user?.shipping_mark}</div>
        </div>
      </div>

      <div className="ch-stats-wrap">
        <div className="ch-stat">
          <p className="ch-stat-value">{summary?.totalItems ?? '-'}</p>
          <p className="ch-stat-label">Total Items</p>
        </div>
        <div className="ch-stat">
          <p className={`ch-stat-value${unpaid > 0 ? ' ch-stat-value--warn' : ''}`}>
            GHS {unpaid > 0 ? unpaid.toFixed(2) : '0'}
          </p>
          <p className="ch-stat-label">Amount Due</p>
        </div>
        <div className="ch-stat">
          <p className="ch-stat-value ch-stat-value--ok">GHS {totalPaid.toFixed(0)}</p>
          <p className="ch-stat-label">Total Paid</p>
        </div>
        <div className="ch-stat">
          <p className="ch-stat-value">{user?.loyalty_points || 0}</p>
          <p className="ch-stat-label">Points</p>
        </div>
      </div>

      {unpaid > 0 && (
        <div className="ch-alert-wrap">
          <div className="ch-alert">
            <div className="ch-alert-text">
              <AlertTriangle size={20} strokeWidth={2} className="ch-alert-icon" />
              <div>
                <p className="ch-alert-title">Outstanding balance</p>
                <p className="ch-alert-amount">GHS {unpaid.toFixed(2)}</p>
              </div>
            </div>
            <Link to="/pay" className="ch-alert-btn">
              Pay Now
            </Link>
          </div>
        </div>
      )}

      {promos.length > 0 && (
        <div className="ch-alert-wrap">
          <ActivePromotionsBanner promotions={promos} audience="customer" />
        </div>
      )}

      <div className="ch-body">
        <div className="ch-grid">
          <div className="ch-col-left">
            <div className="ch-section-header">
              <p className="section-title">Recent Shipments</p>
              <Link to="/items" className="ch-see-all">
                See all
              </Link>
            </div>

            {recent.length > 0 ? (
              <div className="ch-recent-list">
                {recent.map((item) => {
                  const status = STATUS_LABEL[item.status] || { label: item.status, cls: '' };
                  return (
                    <Link to="/items" key={item.id} className="ch-recent-row">
                      <PackageIcon size={18} strokeWidth={1.8} className="ch-recent-icon" />
                      <div className="ch-recent-info">
                        <p className="ch-recent-name">{item.goods_name || 'Shipment item'}</p>
                        <p className="ch-recent-ref">#{item.tracking_number || item.shipping_mark}</p>
                      </div>
                      <span className={`badge ${status.cls}`}>{status.label}</span>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">
                <div className="icon">
                  <Package size={40} strokeWidth={1.4} />
                </div>
                <p style={{ fontWeight: 700, marginBottom: 6 }}>No shipments yet</p>
                <p>
                  When items arrive at our warehouse with your shipping mark <strong>{user?.shipping_mark}</strong>,
                  they will appear here.
                </p>
              </div>
            )}

            <div className="ch-pay-shortcut desktop-only-block">
              <div className="ch-pay-shortcut-info">
                <p className="ch-pay-shortcut-title">Ready to pay shipping fees?</p>
                <p className="ch-pay-shortcut-sub">Pay online or upload a payment screenshot.</p>
              </div>
              <Link to="/pay" className="btn btn-primary">
                Pay Now
              </Link>
            </div>
          </div>

          <div className="ch-col-right">
            <p className="section-title">Quick Actions</p>
            <div className="ch-cards">
              {QUICK_CARDS.map(({ icon: Icon, label, sub, to }) => (
                <Link key={to} to={to} className="ch-card">
                  <span className="ch-card-icon">
                    <Icon size={22} strokeWidth={1.8} />
                  </span>
                  <span className="ch-card-label">{label}</span>
                  <span className="ch-card-sub">{sub}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
